/**
 * AI Chat Mention System - Mention Parser
 *
 * Parses and validates mentions from submitted messages.
 * Resolves mentions to actual data/page references.
 */

import type { ParsedMention, ResolvedMentionData, MentionTrigger } from './types';
import { DataMentionService } from './DataMentionService';
import { pageMentionService } from './PageMentionService';

// ============================================================================
// Types
// ============================================================================

/**
 * Result of parsing mentions from a message
 */
export interface ParsedMessage {
  /** Original input text */
  originalText: string;
  /** Parsed mentions found in the message */
  mentions: ParsedMention[];
  /** Clean text with mentions wrapped in braces and escapes resolved */
  cleanText: string;
}

/**
 * Result of resolving parsed mentions
 */
export interface ResolvedMessage extends ParsedMessage {
  /** Resolved mention data (null if resolution failed) */
  resolvedMentions: (ResolvedMentionData | null)[];
}

/**
 * Unresolved mention with reason
 */
export interface UnresolvedMention {
  /** The mention that failed to resolve */
  mention: ParsedMention;
  /** Descriptive reason for failure */
  reason: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Regex for extracting all mentions from a message
 * Matches @ or # followed by:
 *   - A double-quoted string: #"Page Name With Spaces"
 *   - A single-quoted string: #'Page Name With Spaces'
 *   - Alphanumeric/underscore chars with dot-separated segments: @schema.field
 * Negative lookbehind ensures we don't match escaped triggers (\@ or \#)
 */
const MENTION_REGEX = /(?<!\\)[@#](?:"([^"]+)"|'([^']+)'|([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*))/g;

// ============================================================================
// Functions
// ============================================================================

/**
 * Parse and validate mentions from a submitted message
 *
 * @param message - The raw message text with mentions
 * @returns ParsedMessage with original text, mentions, and cleaned text
 *
 * @example
 * parseMessageMentions('Show @schema.field data on #Dashboard')
 * // Returns {
 * //   originalText: 'Show @schema.field data on #Dashboard',
 * //   mentions: [
 * //     { type: 'data', reference: 'schema.field', rawText: '@schema.field', ... },
 * //     { type: 'page', reference: 'Dashboard', rawText: '#Dashboard', ... }
 * //   ],
 * //   cleanText: 'Show {@schema.field} data on {#Dashboard}'
 * // }
 */
export function parseMessageMentions(message: string): ParsedMessage {
  const mentions: ParsedMention[] = [];
  let match: RegExpExecArray | null;

  // CRITICAL: Reset lastIndex before starting the loop
  MENTION_REGEX.lastIndex = 0;

  while ((match = MENTION_REGEX.exec(message)) !== null) {
    // match[0] is full match including trigger (e.g., '@schema.field' or '#"Page Name"')
    // match[1] = double-quoted content, match[2] = single-quoted content, match[3] = unquoted reference
    const fullMatch = match[0];
    const trigger = fullMatch[0] as MentionTrigger;
    const reference = match[1] || match[2] || match[3];

    mentions.push({
      type: trigger === '@' ? 'data' : 'page',
      reference,
      startPosition: match.index,
      endPosition: match.index + fullMatch.length,
      rawText: fullMatch,
    });
  }

  // Build cleanText:
  // 1. Wrap mentions in braces for AI visibility: @ref -> {@ref}
  // 2. Replace from end to preserve positions
  // 3. Unescape \@ and \# to literal characters
  let cleanText = message;

  // Replace mentions from end to preserve earlier positions
  for (let i = mentions.length - 1; i >= 0; i--) {
    const mention = mentions[i];
    cleanText =
      cleanText.substring(0, mention.startPosition) +
      `{${mention.rawText}}` +
      cleanText.substring(mention.endPosition);
  }

  // Unescape \@ and \# to literal @ and #
  cleanText = cleanText.replace(/\\@/g, '@').replace(/\\#/g, '#');

  return {
    originalText: message,
    mentions,
    cleanText,
  };
}

/**
 * Resolve parsed mentions to actual data
 *
 * @param parsedMessage - The parsed message containing mentions
 * @returns ResolvedMessage with resolved mention data
 *
 * @example
 * const parsed = parseMessageMentions('Show @temp data');
 * const resolved = await resolveMentions(parsed);
 * // resolved.resolvedMentions[0] contains schema/field info or null
 */
export async function resolveMentions(
  parsedMessage: ParsedMessage
): Promise<ResolvedMessage> {
  // Create service instance for data mention resolution
  const dataMentionService = new DataMentionService();

  // Resolve all mentions in parallel
  const resolvedMentions = await Promise.all(
    parsedMessage.mentions.map(async (mention) => {
      if (mention.type === 'data') {
        return dataMentionService.resolveReference(mention.reference);
      } else {
        return pageMentionService.resolveReference(mention.reference);
      }
    })
  );

  return {
    ...parsedMessage,
    resolvedMentions,
  };
}

/**
 * Validate resolved mentions and return unresolved ones with reasons
 *
 * @param resolvedMessage - The resolved message to validate
 * @returns Array of unresolved mentions with descriptive reasons
 */
export function validateMentions(
  resolvedMessage: ResolvedMessage
): UnresolvedMention[] {
  const unresolved: UnresolvedMention[] = [];

  resolvedMessage.mentions.forEach((mention, index) => {
    const resolved = resolvedMessage.resolvedMentions[index];

    if (resolved === null) {
      // Build descriptive reason based on mention path depth
      const pathSegments = mention.reference.split('.').filter(Boolean);
      let reason: string;

      if (mention.type === 'data') {
        if (pathSegments.length === 1) {
          reason = `Schema or group "${pathSegments[0]}" not found`;
        } else if (pathSegments.length === 2) {
          reason = `Schema "${pathSegments[1]}" not found in group "${pathSegments[0]}", or field "${pathSegments[1]}" not found in schema "${pathSegments[0]}"`;
        } else if (pathSegments.length === 3) {
          reason = `Field "${pathSegments[2]}" not found in schema "${pathSegments[1]}"`;
        } else {
          reason = `Invalid data reference path`;
        }
      } else {
        reason = `Page "${mention.reference}" not found`;
      }

      unresolved.push({
        mention,
        reason,
      });
    }
  });

  return unresolved;
}

/**
 * Helper to filter mentions by type
 *
 * @param resolvedMessage - The resolved message
 * @param type - The mention type to filter by ('data' or 'page')
 * @returns Array of resolved mention data for the specified type
 */
export function getMentionsByType(
  resolvedMessage: ResolvedMessage,
  type: 'data' | 'page'
): (ResolvedMentionData | null)[] {
  return resolvedMessage.mentions
    .map((mention, index) => {
      if (mention.type === type) {
        return resolvedMessage.resolvedMentions[index];
      }
      return undefined;
    })
    .filter((m): m is ResolvedMentionData | null => m !== undefined);
}
